import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import type { Bill, BillRun, VerificationComparison, BillRunFlag, BillRunFlagType } from '../api/types';
import { Card, Badge, Alert, DataTable } from '../components/ui';
import type { BadgeTone, DataTableColumn } from '../components/ui';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { BILL_RUN_STATUS_TONE } from '../lib/billRunStatusTone';
import { fmtCurrencyOrDash } from '../lib/fmt';

const FLAG_LABELS: Record<BillRunFlagType, string> = {
  voltage_dropout: 'Voltage dropout',
  current_dropout: 'Current dropout',
  no_prior_run: 'First bill on pocket',
  reading_mismatch: 'Reading mismatch',
  tariff_outdated: 'Tariff outdated',
  // Deliberately says what happened to the BILLS, not what the resolver
  // failed at: "Tariff unresolved" names the worker's internal step, whereas
  // an operator's question is which meters are missing from this run.
  tariff_unresolved: 'Meters not billed',
};

function flagTypeLabel(t: string): string {
  return FLAG_LABELS[t as BillRunFlagType] || t;
}

function renderFlagSummary(type: string, d: Record<string, unknown>): string {
  switch (type) {
    case 'voltage_dropout':
      return `${d.phase} dropped to ${d.period_min} V (period mean ${d.period_mean} V — ${Math.round(((d.ratio as number) || 0) * 100)}% of average) at ${d.min_at}.`;
    case 'current_dropout':
      return `${d.phase} dropped to ${d.period_min} A (period mean ${d.period_mean} A — ${Math.round(((d.ratio as number) || 0) * 100)}% of average) at ${d.min_at}.`;
    case 'no_prior_run':
      return `No prior approved/finalized bill run found on this pocket. This is the first bill — confirm before approving.`;
    case 'reading_mismatch':
      return `Opening reading ${d.opening_reading} does not match the prior bill's closing reading ${d.prior_closing_reading} (Δ ${d.difference}). Prior period ended ${d.prior_period_end}.`;
    case 'tariff_outdated':
      return `This meter's bill used tariff version ${d.resolved_version_id}, but the latest currently effective version is ${d.latest_version_id} (effective from ${d.latest_effective_from}).`;
    default:
      // Production behaviour is deliberately unchanged: stringifying never
      // loses information and never throws, which is the only reason a raw
      // dump is tolerable in front of an operator at all. What it CANNOT do is
      // tell anyone it happened — `tariff_unresolved` rendered like this in
      // production for a full day because a missing case looks like data, not
      // like a defect. This makes the omission loud where it is still cheap to
      // fix, using the fleet's grep-able prefix convention.
      if (import.meta.env.DEV) {
        console.error(
          `FLAG_SUMMARY_UNHANDLED: no renderFlagSummary case for flag_type "${type}" ` +
            '— it is rendering as raw JSON to the operator. Add a case (and a ' +
            'BillRunFlagType member, which forces a FLAG_LABELS entry).',
        );
      }
      return JSON.stringify(d);
  }
}

/**
 * One skipped meter as the `tariff_unresolved` flag records it.
 *
 * `reason_label` and the remedies are taken from the payload rather than
 * re-derived from the raw `reason` key, because the label map lives in
 * `workers/bill-calculator/src/tariffResolution.ts` (SKIP_REASON_LABELS /
 * SKIP_REASON_REMEDIES). Copying those five strings into the SPA would make
 * this the second copy of a vocabulary that must agree with the biller — the
 * duplication class CLAUDE.md §7 flags. The payload already carries the
 * resolved label per meter, so nothing needs to be duplicated.
 */
interface UnresolvedMeter {
  meter_name: string;
  reason_label: string;
  uncovered_days: string[];
}

function parseUnresolvedMeters(v: unknown): UnresolvedMeter[] {
  if (!Array.isArray(v)) return [];
  const out: UnresolvedMeter[] = [];
  for (const raw of v) {
    if (typeof raw !== 'object' || raw === null) continue;
    const m = raw as Record<string, unknown>;
    const name =
      typeof m.meter_name === 'string' && m.meter_name.trim() !== ''
        ? m.meter_name
        : typeof m.meter_asset_id === 'string' && m.meter_asset_id.trim() !== ''
          ? m.meter_asset_id
          : 'Unknown meter';
    out.push({
      meter_name: name,
      reason_label:
        typeof m.reason_label === 'string' && m.reason_label.trim() !== ''
          ? m.reason_label
          : 'No tariff could be resolved for this meter',
      uncovered_days: Array.isArray(m.uncovered_days)
        ? m.uncovered_days.filter((d): d is string => typeof d === 'string')
        : [],
    });
  }
  return out;
}

function stringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];
}

/** Shared by both the one-meter and many-meter branches. */
function RemedyList({ remedies }: { remedies: string[] }) {
  if (remedies.length === 0) return null;
  return (
    <p className="text-sm text-content-muted">To fix: {remedies.join('; ')}.</p>
  );
}

/**
 * The `tariff_unresolved` flag body.
 *
 * Three things shape this and none of them are cosmetic.
 *
 * 1. A run can hold 98+ meters and every one of them can be unresolved, so a
 *    per-meter line CANNOT be the always-rendered form — per Beau, a list that
 *    long "could go off screen and turn into a mess". The count and the
 *    per-reason breakdown lead; the per-meter detail is one click away.
 * 2. The disclosure is React state, NOT a native <details>. That is a testing
 *    decision: <details> keeps its content in the DOM when closed, so a jsdom
 *    assertion that the list is "not rendered inline" would pass whether or
 *    not the collapse worked — a vacuous guard of exactly the kind this repo
 *    has been bitten by. Conditional rendering makes collapsed mean absent,
 *    so the test can actually fail.
 * 3. Only `text-content` / `text-content-muted` appear here — the two token
 *    pairs the enclosing amber box already asserts in contrast.test.ts
 *    (criticalAmber950). No Badge and no `whitespace-nowrap` either: this
 *    subtree sits in the row's `flex-1 min-w-0` item, and one nowrap chip
 *    would reintroduce a min-content floor for the whole row.
 */
function TariffUnresolvedSummary({ d }: { d: Record<string, unknown> }) {
  const [showMeters, setShowMeters] = useState(false);

  const meters = parseUnresolvedMeters(d.meters);
  const remedies = stringArray(d.remedies);
  const periodStart = typeof d.period_start === 'string' ? d.period_start : null;
  const periodEnd = typeof d.period_end === 'string' ? d.period_end : null;
  const fullPeriod = periodStart && periodEnd ? `${periodStart} to ${periodEnd}` : null;

  // `meters` unusable (absent, or not an array of objects). Degrade to the
  // count — still a sentence, never a payload dump.
  if (meters.length === 0) {
    const n = typeof d.skipped_meter_count === 'number' ? d.skipped_meter_count : null;
    return (
      <div className="mt-1 space-y-1">
        <p className="text-sm text-content">
          {n === null
            ? 'Some meters were not billed: no tariff could be resolved for them.'
            : `${n} ${n === 1 ? 'meter was' : 'meters were'} not billed: no tariff could be resolved` +
              `${fullPeriod ? ` for ${fullPeriod}` : ''}.`}
        </p>
        <RemedyList remedies={remedies} />
      </div>
    );
  }

  // ONE meter — the tight sentence the product owner asked for. No count, no
  // breakdown, no disclosure: naming the meter and the problem is the whole
  // message.
  if (meters.length === 1) {
    const m = meters[0];
    // The unpriced span earns words only when it is NARROWER than the run's
    // period. For a whole-period skip it just repeats the run header; for a
    // mid-chain version gap it is the one fact not derivable from anything
    // else on screen.
    const span = m.uncovered_days.join('; ');
    const spanClause = span !== '' && span !== fullPeriod ? ` Unpriced: ${span}.` : '';
    return (
      <div className="mt-1 space-y-1">
        <p className="text-sm text-content">
          Meter {m.meter_name} was not billed. {m.reason_label}.{spanClause}
        </p>
        <RemedyList remedies={remedies} />
      </div>
    );
  }

  // MANY meters — count first, then how many fell to each reason, then the
  // remedies. The reasons are a fixed set (TariffSkipReason), so this section
  // has a fixed ceiling no matter how many meters the run holds.
  const byLabel = new Map<string, number>();
  for (const m of meters) {
    byLabel.set(m.reason_label, (byLabel.get(m.reason_label) ?? 0) + 1);
  }

  return (
    <div className="mt-1 space-y-2">
      <p className="text-sm text-content">
        {meters.length} meters were not billed
        {fullPeriod ? ` for ${fullPeriod}` : ''}.
      </p>
      <ul className="space-y-0.5 text-sm text-content-muted">
        {[...byLabel.entries()].map(([label, n]) => (
          <li key={label}>
            {n} × {label}
          </li>
        ))}
      </ul>
      <RemedyList remedies={remedies} />
      <button
        type="button"
        onClick={() => setShowMeters(v => !v)}
        aria-expanded={showMeters}
        className="tap-target lg:min-h-0 justify-start text-sm font-medium text-content underline"
      >
        {showMeters ? 'Hide meters' : `Show ${meters.length} meters`}
      </button>
      {showMeters && (
        <ul className="space-y-1 text-sm text-content-muted">
          {meters.map((m, i) => {
            const span = m.uncovered_days.join('; ');
            return (
              // Index key: two meters can legitimately share a display name,
              // and this list is derived from an immutable payload — it is
              // never reordered or spliced, so the index is stable.
              <li key={i}>
                <span className="text-content">{m.meter_name}</span> — {m.reason_label}
                {span !== '' && span !== fullPeriod ? ` (unpriced: ${span})` : ''}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
import BillRunDashboardComponent from '../components/BillRunDashboard';

export default function BillRunDetail() {
  const { id } = useParams<{ id: string }>();
  const [billRun, setBillRun] = useState<BillRun | null>(null);
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rerunning, setRerunning] = useState(false);
  const [showRerunModal, setShowRerunModal] = useState(false);
  const [rerunReason, setRerunReason] = useState('');
  const [flags, setFlags] = useState<BillRunFlag[]>([]);
  const [ackComments, setAckComments] = useState<Record<string, string>>({});
  const [ackingId, setAckingId] = useState<string | null>(null);
  const [comparisons, setComparisons] = useState<VerificationComparison[]>([]);
  const verifyPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [generatingPdfs, setGeneratingPdfs] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  // Drives the Bills table's density only — same hook and same `lg` query
  // DataTable uses to decide whether to collapse, so the two cannot disagree.
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (id) {
      loadData();
    }
    return () => { if (verifyPollRef.current) clearInterval(verifyPollRef.current); };
  }, [id]);

  // Poll while verification is in progress
  useEffect(() => {
    if (billRun?.verification_mode === 1 && billRun?.verification_status === 'comparing') {
      verifyPollRef.current = setInterval(async () => {
        try {
          const res = await api.getBillRun(id!);
          setBillRun(res.data);
          if (res.data.verification_status === 'complete') {
            if (verifyPollRef.current) clearInterval(verifyPollRef.current);
            loadComparisons();
          }
        } catch {}
      }, 3000);
    }
    return () => { if (verifyPollRef.current) clearInterval(verifyPollRef.current); };
  }, [billRun?.verification_status]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [runRes, billsRes, flagsRes] = await Promise.all([
        api.getBillRun(id!),
        api.getBills({ bill_run_id: id }),
        api.getBillRunFlags(id!).catch(() => ({ data: [] as BillRunFlag[] })),
      ]);

      setBillRun(runRes.data);
      setBills(billsRes.data);
      setFlags(flagsRes.data);

      // Load comparisons if verification is complete
      if (runRes.data.verification_mode === 1 && runRes.data.verification_bill_id && runRes.data.verification_status === 'complete') {
        loadComparisons(runRes.data.verification_bill_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bill run');
    } finally {
      setLoading(false);
    }
  };

  const loadComparisons = async (vBillId?: string) => {
    const verificationBillId = vBillId || billRun?.verification_bill_id;
    if (!verificationBillId) return;
    try {
      const res = await api.getVerificationComparison(verificationBillId);
      setComparisons(res.data);
    } catch (err) {
      console.error('Failed to load comparisons:', err);
    }
  };

  const handleApprove = async () => {
    try {
      setApproving(true);
      setError(null);
      await api.approveBillRun(id!);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve bill run');
    } finally {
      setApproving(false);
    }
  };

  const handleFinalize = async () => {
    try {
      setFinalizing(true);
      setError(null);
      await api.finalizeBillRun(id!);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to finalize bill run');
    } finally {
      setFinalizing(false);
    }
  };

  const handleReject = async () => {
    try {
      setRejecting(true);
      setError(null);
      await api.rejectBillRun(id!, rejectReason || undefined);
      setShowRejectModal(false);
      setRejectReason('');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject bill run');
    } finally {
      setRejecting(false);
    }
  };

  const handleAcknowledgeFlag = async (flagId: string) => {
    const comment = (ackComments[flagId] || '').trim();
    if (!comment) return;
    try {
      setAckingId(flagId);
      setError(null);
      await api.acknowledgeBillRunFlag(id!, flagId, comment);
      setAckComments(prev => { const next = { ...prev }; delete next[flagId]; return next; });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge flag');
    } finally {
      setAckingId(null);
    }
  };

  // Bulk-ack: requires every outstanding flag to have a non-empty comment.
  // Refresh once at the end (vs once per ack) — that's the UX win on long
  // pre-check lists. Serial issue so a mid-list failure leaves earlier acks
  // committed and surfaces the failing flag clearly.
  const [ackingAll, setAckingAll] = useState(false);
  const handleAcknowledgeAll = async () => {
    const outstanding = flags.filter(f => !f.acknowledged_at);
    if (outstanding.length === 0) return;
    if (outstanding.some(f => !(ackComments[f.id] || '').trim())) return;
    try {
      setAckingAll(true);
      setError(null);
      for (const flag of outstanding) {
        const comment = (ackComments[flag.id] || '').trim();
        await api.acknowledgeBillRunFlag(id!, flag.id, comment);
      }
      setAckComments(prev => {
        const next = { ...prev };
        for (const flag of outstanding) delete next[flag.id];
        return next;
      });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge flags');
    } finally {
      setAckingAll(false);
    }
  };

  const handleRerun = async () => {
    const reason = rerunReason.trim();
    if (!reason) return;
    try {
      setRerunning(true);
      setError(null);
      await api.rerunBillRun(id!, reason);
      setShowRerunModal(false);
      setRerunReason('');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to re-run bill run');
    } finally {
      setRerunning(false);
    }
  };

  const handleGeneratePdf = async () => {
    try {
      setGeneratingPdfs(true);
      setPdfError(null);
      // billing-api clears bill_run.pdf_url server-side before queuing, so
      // "pdf_url is set" reliably indicates *this* generation finished.
      await api.generateBillRunPdfs(id!);
      const pollInterval = setInterval(async () => {
        try {
          const res = await api.getBillRun(id!);
          setBillRun(res.data);
          if (res.data.pdf_url) {
            clearInterval(pollInterval);
            setGeneratingPdfs(false);
          }
        } catch {}
      }, 1000);
      setTimeout(() => { clearInterval(pollInterval); setGeneratingPdfs(false); }, 120000);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Failed to generate PDF');
      setGeneratingPdfs(false);
    }
  };

  const handleViewRunPdf = async () => {
    try {
      setPdfError(null);
      const { url } = await api.getBillRunPdfUrl(id!);
      window.open(url, '_blank');
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Failed to get PDF URL');
    }
  };

  const handleDownloadRunPdf = async () => {
    try {
      setPdfError(null);
      const { downloadUrl } = await api.getBillRunPdfUrl(id!);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Failed to get PDF URL');
    }
  };

  // Converged onto the shared formatter (src/lib/fmt.ts), which is where the
  // non-breaking separator after "R" lives — the hand-rolled ASCII space was
  // the only break opportunity in the string and orphaned the "R" onto its own
  // line in narrow table columns.
  const formatCurrency = fmtCurrencyOrDash;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatNumber = (num: number | null | undefined, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('en-ZA', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  // Routed through ui/Badge. The bill-RUN half of this map is now the shared
  // one from src/lib/billRunStatusTone.ts rather than a seventh hand-copy;
  // this copy had silently dropped `error: 'danger'`, so a FAILED bill run
  // fell through the `?? 'neutral'` fallback and rendered as a grey "no news"
  // chip on its own detail page while /bill-runs rendered the same run red.
  // Identical defect to the one Dashboard.tsx shipped — which is the whole
  // argument for the shared module.
  //
  // The spread adds the two BILL-level statuses this renderer also serves
  // (getStatusBadge is used for both `billRun.status` and `bill.status`);
  // they are deliberately NOT in the shared module, which is bill-RUN
  // statuses only. Neither key collides with a bill-run status, so the
  // spread order is not load-bearing.
  const STATUS_TONE: Record<string, BadgeTone> = {
    ...BILL_RUN_STATUS_TONE,
    calculated: 'neutral',
    adjusted: 'orange',
  };
  const getStatusBadge = (status: string) => (
    <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{status.replace('_', ' ')}</Badge>
  );

  // Bills table: no selection, but there IS a rowActions "Details" cell plus
  // DataTable's own expand toggle — the same two extra non-data cells as
  // BillRunsTable's migration, so Meter (identifying) + Total (the number
  // people scan for) is the ceiling; Building/kWh/Max kVA/kVArh/Status are
  // one tap away.
  const billsColumns: DataTableColumn<Bill>[] = [
    {
      key: 'meter',
      label: 'Meter',
      priority: 'primary',
      render: (bill) => (
        <>
          <Link to={`/bills/${bill.id}`} className="text-accent hover:opacity-90 font-medium">
            {bill.meter_name || bill.meter_asset_id.slice(0, 12)}
          </Link>
          {bill.tenant_name_snapshot && (
            /* `flex-wrap` (added 2026-07-31). This row is the Meter column's
               real width floor below `lg`, not the meter name above it. A
               non-wrapping flex row's min-content width is the SUM of its
               items' min-contents, so the tenant chip (which is a nowrap-ish
               pill: px-1.5 + a 6px dot + gap + the name) and the
               "2026-06-01 → 2026-06-30" segment span — whose two dates are
               each a 10-character unbreakable token — were forced side by
               side at every width, ~150px of floor in a column that has
               ~110px to spend at 375px. Wrapping lets the segment drop under
               the chip, so the floor becomes the WIDER of the two (~75px)
               instead of their sum. Purely a wrap permission: at `lg` there
               is width for both and they stay on one line, so desktop is
               unchanged. */
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                bill.tenant_id ? 'bg-green-50 text-green-800 dark:bg-green-900 dark:text-green-100' : 'bg-amber-50 text-amber-800 dark:bg-amber-900 dark:text-amber-100'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${
                  bill.tenant_id ? 'bg-green-500' : 'bg-amber-500'
                }`} />
                {bill.tenant_name_snapshot}
              </span>
              {bill.segment_start && bill.segment_end && (
                <span className="text-content-muted">
                  {bill.segment_start} → {bill.segment_end}
                </span>
              )}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'building',
      label: 'Building',
      priority: 'secondary',
      render: (bill) => <span className="text-content-muted">{bill.building_name || '-'}</span>,
    },
    {
      // A bill run can be MIXED (water + electricity in one run — demo has 8
      // such pockets), so a `kWh` HEADER cannot be right for every row. The
      // unit moves into the cell, where it travels with the value it
      // describes, and the header becomes unit-free. Same shape as
      // insights/templates/LoadCostProfile.tsx's per-row `{m.unit}`.
      key: 'consumption',
      label: 'Consumption',
      priority: 'secondary',
      numeric: true,
      render: (bill) => (
        <span className="font-mono">{formatNumber(bill.delta_kwh)} {bill.consumption_unit}</span>
      ),
    },
    {
      // kVA and kVArh are electricity-only. A water row's stored 0 is an
      // absence, not a measurement, so it renders as an em-dash rather than
      // "0,00" — the same treatment LoadCostProfile.tsx:70 already gives
      // Peak kVA for water. The columns stay because a mixed run still has
      // electricity rows that need them.
      key: 'max_kva',
      label: 'Max kVA',
      priority: 'secondary',
      numeric: true,
      render: (bill) => (
        <span className="font-mono">{bill.utility === 'water' ? '—' : formatNumber(bill.max_kva)}</span>
      ),
    },
    {
      key: 'kvarh',
      label: 'kVArh',
      priority: 'secondary',
      numeric: true,
      render: (bill) => (
        <span className="font-mono">{bill.utility === 'water' ? '—' : formatNumber(bill.reactive_kvarh)}</span>
      ),
    },
    {
      key: 'total',
      label: 'Total',
      priority: 'primary',
      numeric: true,
      render: (bill) => <span className="font-semibold">{formatCurrency(bill.total_amount_cents)}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      priority: 'secondary',
      render: (bill) => getStatusBadge(bill.status),
    },
  ];

  // Totals are computed PER UTILITY. Summing `delta_kwh` across every bill was
  // the one place in the SPA that produced a wrong NUMBER rather than a wrong
  // label: `delta_kwh` holds kL on a water bill, so a mixed run added water
  // volume to electrical energy and printed the result as "kWh".
  //
  // Splitting rather than picking one unit means a homogeneous run is
  // numerically UNCHANGED — an all-electricity run's kWh total is the same sum
  // over the same bills, because no water bill was contributing to it. An
  // all-water run shows the same figure it always did, now labelled kL. Only a
  // genuinely mixed run's display changes, and there the old single figure was
  // not a quantity of anything.
  //
  // Same split-by-utility shape as BillRunDashboard.tsx:107-112, which is the
  // reference implementation for a mixed run.
  const elecBills = bills.filter((b) => b.utility !== 'water');
  const waterBills = bills.filter((b) => b.utility === 'water');
  const totalKwh = elecBills.reduce((sum, b) => sum + (b.delta_kwh || 0), 0);
  const totalKl = waterBills.reduce((sum, b) => sum + (b.delta_kwh || 0), 0);
  // Guarded by the `elecBills.length > 0` render gate below: Math.max() over an
  // empty list is -Infinity, which would print as "-∞" on an all-water run.
  const maxKva = elecBills.length > 0 ? Math.max(...elecBills.map((b) => b.max_kva || 0)) : 0;
  const totalKvarh = elecBills.reduce((sum, b) => sum + (b.reactive_kvarh || 0), 0);

  if (loading) {
    return (
      /* `p-4 lg:p-8` on all three of this page's wrappers — desktop
         unchanged, 32px of a 375px viewport handed back below `lg`. Full
         rationale on BillRuns.tsx's wrapper: the whole branch sized its
         column splits against "~343px usable", which assumed 16px gutters
         this app never actually had. */
      <div className="p-4 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-border-token rounded w-64"></div>
          <div className="h-48 bg-border-token rounded-xl"></div>
          <div className="h-96 bg-border-token rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (error || !billRun) {
    return (
      <div className="p-4 lg:p-8">
        <Alert tone="danger" className="text-center">
          <p>{error || 'Bill run not found'}</p>
          <Link
            to="/bill-runs"
            className="inline-block mt-4 px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors"
          >
            Back to Bill Runs
          </Link>
        </Alert>
      </div>
    );
  }

  const unackedFlags = flags.filter(f => !f.acknowledged_at);
  // canApprove keeps the Reject / Re-Run / Approve trio visible while pending;
  // approveBlocked separately disables only the Approve button until every
  // pre-check flag has been acknowledged.
  const canApprove = billRun.status === 'pending_approval';
  const approveBlocked = unackedFlags.length > 0;
  const canFinalize = billRun.status === 'approved';

  return (
    <div className="p-4 lg:p-8">
      {/* Breadcrumb */}
      <div className="mb-4">
        <Link to="/bill-runs" className="text-accent hover:opacity-90 text-sm">
          &larr; Back to Bill Runs
        </Link>
      </div>

      {/* Header. flex-wrap + gap-4: at 375px the title block and the
          Reject/Re-Run/Approve group cannot share a row, and without wrapping
          the action group is squeezed into a sliver against the right edge —
          which is what put spec §8.3 ("Bill Runs is fully operable one-handed
          on a 375px viewport: list, filter, open a run, approve") out of
          reach. justify-between still puts them at opposite ends whenever
          they do fit, so desktop is unchanged; gap-4 only ever applies as a
          minimum separation. */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-content">
            Bill Run: {formatDate(billRun.period_start)} - {formatDate(billRun.period_end)}
          </h1>
          <p className="text-content-muted mt-1">
            Created {formatDate(billRun.created_at)} | {bills.length} bills
          </p>
          {/* The level the run's water bills were priced at (spec Decision 11); nothing at Water Wise. */}
          {(billRun.water_restrictions ?? []).map((r) => (
            <p key={`${r.municipality_name}-${r.level}`} className="mt-1 text-sm font-medium text-content">{r.notice}</p>
          ))}
          {(billRun.rerun_count ?? 0) > 0 && billRun.last_rerun_at && (
            /* Final tidy (dark-mode contrast fix): text-amber-700 on this
               page's surface-sunken(dark) body is 3.93:1 — fails AA.
               dark:text-amber-300 clears it at 13.70:1. */
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
              <span className="font-medium">Last re-run:</span>{' '}
              {new Date(billRun.last_rerun_at + 'Z').toLocaleString('en-ZA', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
              {billRun.last_rerun_reason && (
                <> — &ldquo;{billRun.last_rerun_reason}&rdquo;</>
              )}
              {(billRun.rerun_count ?? 0) > 1 && (
                <span className="ml-2 text-xs text-amber-600">
                  ({billRun.rerun_count} re-runs total)
                </span>
              )}
            </p>
          )}
        </div>
        {/* flex-wrap so the trio stacks rather than overflowing once the
            group itself is narrower than three 44px-floored buttons. */}
        <div className="flex flex-wrap items-center gap-3">
          {canApprove && (
            <>
              <button
                onClick={() => setShowRejectModal(true)}
                className="tap-target gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Reject
              </button>
              <button
                onClick={() => setShowRerunModal(true)}
                className="tap-target gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                title="Recalculate this bill run with current attribute values"
              >
                Re-Run
              </button>
              <button
                onClick={handleApprove}
                disabled={approving || approveBlocked}
                title={approveBlocked
                  ? `Acknowledge ${unackedFlags.length} pre-check flag${unackedFlags.length === 1 ? '' : 's'} before approving`
                  : undefined}
                className="tap-target gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {approving && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                Approve
              </button>
            </>
          )}
          {canFinalize && (
            <button
              onClick={handleFinalize}
              disabled={finalizing}
              className="tap-target gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {finalizing && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              Finalize
            </button>
          )}
          {billRun.status === 'finalized' && (
            billRun.pdf_url ? (
              <>
                <button
                  onClick={handleViewRunPdf}
                  className="tap-target gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  View Bill
                </button>
                <button
                  onClick={handleDownloadRunPdf}
                  className="tap-target gap-2 px-4 py-2 bg-surface-sunken text-content rounded-lg hover:bg-border-token transition-colors"
                >
                  Download PDF
                </button>
                <button
                  onClick={handleGeneratePdf}
                  disabled={generatingPdfs}
                  className="tap-target gap-2 px-4 py-2 bg-surface-sunken text-content rounded-lg hover:bg-border-token transition-colors disabled:opacity-50"
                  title="Regenerate the PDF (e.g. after a tariff or layout change)"
                >
                  {generatingPdfs ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Regenerating...
                    </>
                  ) : 'Regenerate'}
                </button>
              </>
            ) : (
              <button
                onClick={handleGeneratePdf}
                disabled={generatingPdfs}
                className="tap-target gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {generatingPdfs ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating...
                  </>
                ) : 'Generate PDF'}
              </button>
            )
          )}
          {billRun.status === 'error' && billRun.error_message?.startsWith('Rejected:') ? (
            <Badge tone="orange" title={billRun.error_message.replace('Rejected: ', '')}>
              Rejected
            </Badge>
          ) : getStatusBadge(billRun.status)}
        </div>
      </div>

      {/* Pre-check flags */}
      {flags.length > 0 && (() => {
        // Bulk-ack gate: every outstanding flag must already have a comment
        // typed. We *don't* fill in a default — that would invite users to
        // hammer the button blindly. Tooltip surfaces what's still missing.
        const missingCount = unackedFlags.filter(f => !(ackComments[f.id] || '').trim()).length;
        const canAckAll = unackedFlags.length > 1 && missingCount === 0 && !ackingAll;
        return (
        /* C2 (dark-mode contrast fix): dark:bg-amber-950, not the usual
           dark:bg-amber-900 (see BillAuditPanel.tsx/CommissionPage.tsx
           convention) — this box's every flag row renders text-content-muted
           (meter name tag, "Acked ..." line) directly on the fill, and
           amber-900 (#78350f) only clears 3.60:1 against textMuted
           (#a8a29e), failing AA. amber-950 (#451a03) clears both textMuted
           (5.94:1) and text (14.34:1) — both asserted in
           src/test/contrast.test.ts (criticalAmber950). Distinctness from
           the page's own
           surface-sunken(dark) background is thinner than the 900 shade
           (1.32:1 vs 2.18:1) but still above the "not invisible" bar this
           codebase uses (>1.2, see contrast.test.ts's badge-vs-card check)
           and the box's own border keeps it delineated regardless. */
        <div className={`mb-6 rounded-xl border ${
          unackedFlags.length > 0 ? 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950' : 'border-border-token bg-surface-sunken'
        }`}>
          <div className={`px-4 py-3 border-b ${unackedFlags.length > 0 ? 'border-amber-200 dark:border-amber-800' : 'border-border-token'}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-content">
                  Pre-check flags
                  {unackedFlags.length > 0 ? (
                    <Badge tone="warning" className="ml-2">{unackedFlags.length} unacknowledged</Badge>
                  ) : (
                    <Badge tone="success" className="ml-2">All acknowledged</Badge>
                  )}
                </h2>
                <p className="text-xs text-content-muted mt-0.5">
                  Automated checks raised at calculation time. Each must be acknowledged with a comment before approving.
                </p>
              </div>
              {unackedFlags.length > 1 && (
                <button
                  type="button"
                  onClick={handleAcknowledgeAll}
                  disabled={!canAckAll}
                  title={
                    missingCount > 0
                      ? `Fill in ${missingCount} more comment${missingCount === 1 ? '' : 's'} to enable bulk acknowledge`
                      : 'Acknowledge every outstanding flag in one go'
                  }
                  className="shrink-0 px-3 py-1.5 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {ackingAll
                    ? 'Acknowledging…'
                    : missingCount > 0
                      ? `Acknowledge All (${missingCount} missing)`
                      : `Acknowledge All (${unackedFlags.length})`}
                </button>
              )}
            </div>
          </div>
          <div className="divide-y divide-border-token max-h-96 overflow-y-auto">
            {flags.map(flag => {
              const detailObj = (() => { try { return JSON.parse(flag.details); } catch { return {}; } })() as Record<string, unknown>;
              const meterName = (detailObj.meter_name as string | undefined) || flag.meter_asset_id || null;
              const acked = !!flag.acknowledged_at;
              return (
                <div key={flag.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge tone={acked ? 'neutral' : 'warning'}>{flagTypeLabel(flag.flag_type)}</Badge>
                        {meterName && <span className="text-xs text-content-muted">{meterName}</span>}
                      </div>
                      {/* `tariff_unresolved` is the one flag whose body is
                          structural rather than a sentence — it has to scale
                          from 1 meter to 98 without becoming a wall, which a
                          single string cannot do. Every other type stays on
                          the established one-sentence `renderFlagSummary`
                          path. */}
                      {flag.flag_type === 'tariff_unresolved' ? (
                        <TariffUnresolvedSummary d={detailObj} />
                      ) : (
                        <p className="mt-1 text-sm text-content">
                          {renderFlagSummary(flag.flag_type, detailObj)}
                        </p>
                      )}
                      {acked && (
                        <p className="mt-1 text-xs text-content-muted">
                          Acked {flag.acknowledged_at} — &ldquo;{flag.acknowledgement_comment}&rdquo;
                        </p>
                      )}
                    </div>
                    {!acked && (
                      <div className="flex flex-col gap-2 w-72">
                        <textarea
                          value={ackComments[flag.id] || ''}
                          onChange={e => setAckComments(prev => ({ ...prev, [flag.id]: e.target.value }))}
                          placeholder="Acknowledgement comment (required)"
                          rows={2}
                          className="px-2 py-1 border border-amber-300 rounded text-sm focus:ring-2 focus:ring-amber-500"
                        />
                        <button
                          onClick={() => handleAcknowledgeFlag(flag.id)}
                          disabled={ackingId === flag.id || !(ackComments[flag.id] || '').trim()}
                          className="px-3 py-1 bg-amber-600 text-white rounded text-xs hover:bg-amber-700 disabled:opacity-50"
                        >
                          {ackingId === flag.id ? 'Saving…' : 'Acknowledge'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        );
      })()}

      {/* Dashboard */}
      {bills.length > 0 && ['pending_approval', 'approved', 'finalized'].includes(billRun.status) && (
        <BillRunDashboardComponent billRunId={billRun.id} />
      )}

      {/* Error */}
      {(error || pdfError) && (
        <Alert tone="danger" className="mb-6">
          {error || pdfError}
        </Alert>
      )}

      {/* Verification Section */}
      {billRun.verification_mode === 1 && (
        <Card className="shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-content">Bill Verification</h2>
            {(() => {
              if (billRun.verification_status !== 'complete') {
                // 'confirmed' (was indigo-100/800) -> success, same hue
                // normalization as the identical state in BillVerification.tsx.
                const tone: BadgeTone = billRun.verification_status === 'comparing' ? 'info' :
                  billRun.verification_status === 'confirmed' ? 'success' :
                  billRun.verification_status === 'pending_review' ? 'warning' :
                  'neutral';
                const label = billRun.verification_status === 'comparing' ? 'Calculating & Comparing...' :
                  billRun.verification_status === 'confirmed' ? 'Values Confirmed' :
                  billRun.verification_status === 'pending_review' ? 'Awaiting Review' : 'Processing';
                return <Badge tone={tone}>{label}</Badge>;
              }
              // Color based on comparison results — major/minor/all-match
              // variance, the same danger/warning/success ladder used for the
              // per-row comparison status chips below.
              const hasMajor = comparisons.some(c => c.status === 'major_variance');
              const hasMinor = comparisons.some(c => c.status === 'minor_variance');
              const tone: BadgeTone = hasMajor ? 'danger' : hasMinor ? 'warning' : 'success';
              const label = hasMajor ? 'Discrepancies Found' :
                hasMinor ? 'Minor Variances' :
                'Verified - All Match';
              return <Badge tone={tone}>{label}</Badge>;
            })()}
          </div>

          {billRun.verification_status === 'comparing' && (
            <Alert tone="info">
              <div className="flex items-center gap-3">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Calculating bills and comparing against council bill values...</span>
              </div>
            </Alert>
          )}

          {billRun.verification_status === 'complete' && comparisons.length > 0 && (
            <div>
              {/* Summary stats */}
              {(() => {
                const matches = comparisons.filter(c => c.status === 'match').length;
                const total = comparisons.length;
                const totalRow = comparisons.find(c => c.charge_category === 'total');
                return (
                  /* `grid-cols-1 sm:grid-cols-3`, not a flat grid-cols-3.
                     A `1fr` track is `minmax(auto, 1fr)`, so each tile's
                     min-content is a hard floor the grid cannot go under —
                     and the third tile holds a text-2xl (24px) currency
                     string whose digits are joined by U+00A0 group
                     separators (en-ZA), i.e. one unbreakable ~140px token.
                     Three of those plus p-3 and gaps floor this grid near
                     530px, and unlike the comparison table below it there is
                     no overflow-x wrapper — it pushed the Card, and the page,
                     wider than the viewport. Stacking below `sm` removes the
                     floor entirely; by `sm` (640px) there is room for all
                     three. Verification runs only, which is why the earlier
                     sweeps did not see it. */
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    {/* Hand-rolled (not a status chip — a KPI tile), so this
                        keeps its own green hue rather than routing through
                        Badge. dark: added because this tile's literal
                        bg-green-50 previously had nowhere to go dark — inside
                        the new <Card> it would otherwise sit as a near-white
                        patch on the dark surface (ratio 14.49:1 unstyled).
                        green-900 is 1.66:1 vs surfaceRaised(dark), clearing
                        the >1.2 "visually distinct from its own card"
                        threshold; green-300/green-400 text on it are 6.49:1
                        / 5.23:1, both clearing AA (4.5:1). */}
                    <div className="bg-green-50 dark:bg-green-900 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-700 dark:text-green-300">{matches}/{total}</p>
                      <p className="text-xs text-green-600 dark:text-green-400">Categories Match</p>
                    </div>
                    <div className="bg-surface-sunken rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-content">
                        {totalRow ? `${totalRow.difference_percent?.toFixed(1)}%` : '-'}
                      </p>
                      <p className="text-xs text-content-muted">Total Variance</p>
                    </div>
                    <div className="bg-surface-sunken rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-content">
                        {totalRow ? formatCurrency(totalRow.difference_cents) : '-'}
                      </p>
                      <p className="text-xs text-content-muted">Total Difference</p>
                    </div>
                  </div>
                );
              })()}

              {/* Comparison table. 6 columns (Category, Council Bill,
                  Calculated, Difference, %, Status) — the review's count of 7
                  was one high. `min-w-max` on the >=6-column rule: with plain
                  `w-full` and auto table-layout the table only exceeds its
                  wrapper once the SUM of column min-content widths does, so
                  at 375px (~343px usable) six columns of currency plus a
                  Badge just word-wrap into slivers and the `overflow-x-auto`
                  above never engages. Forcing max-content width makes the
                  wrapper genuinely scroll. Same reasoning as
                  DataTable.tsx:104-121, which documents it in full. */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-max text-sm">
                  <thead className="bg-surface-sunken">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-muted uppercase">Category</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-content-muted uppercase">Council Bill</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-content-muted uppercase">Calculated</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-content-muted uppercase">Difference</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-content-muted uppercase">%</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-content-muted uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-token">
                    {comparisons.map((comp) => (
                      <tr key={comp.id} className={comp.charge_category === 'total' ? 'bg-surface-sunken font-semibold' : ''}>
                        <td className="px-4 py-2 capitalize">{comp.charge_category.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-2 text-right font-mono">{formatCurrency(comp.council_value_cents)}</td>
                        <td className="px-4 py-2 text-right font-mono">{formatCurrency(comp.calculated_value_cents)}</td>
                        <td className="px-4 py-2 text-right font-mono">{formatCurrency(comp.difference_cents)}</td>
                        <td className="px-4 py-2 text-right font-mono">{comp.difference_percent?.toFixed(1)}%</td>
                        <td className="px-4 py-2 text-center">
                          <Badge tone={comp.status === 'match' ? 'success' : comp.status === 'minor_variance' ? 'warning' : 'danger'}>
                            {comp.status === 'match' ? 'Match' :
                             comp.status === 'minor_variance' ? 'Minor' : 'Major'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Bills Table */}
      <Card className="shadow-sm">
        <div className="px-6 py-4 border-b border-border-token">
          <h2 className="text-lg font-semibold text-content">Bills</h2>
        </div>

        {bills.length === 0 ? (
          <div className="px-6 py-12 text-center text-content-muted">
            <p>No bills in this run yet</p>
          </div>
        ) : (
          <div>
            <DataTable
              data={bills}
              columns={billsColumns}
              rowKey={(bill) => bill.id}
              /* Per-breakpoint density, same reasoning and same hook as
                 BillRunsTable — `comfortable` at `lg`+ (desktop untouched),
                 `compact` below it, where px-5 on both surviving columns
                 spent 32px this page does not have. */
              density={isDesktop ? 'comfortable' : 'compact'}
              /* `lg:min-h-0`: 44px is a TOUCH floor; in a table row it drives
                 row height and costs rows-per-screen on desktop. Applies to
                 this row action ONLY — the page-level approve/reject/finalize
                 CTAs above deliberately keep the full 44px, which is the right
                 size for a primary button on desktop too. Full rationale on
                 Tenants.tsx's rowActions; escape at tailwind.config.js:103. */
              rowActions={(bill) => (
                <Link to={`/bills/${bill.id}`} className="tap-target lg:min-h-0 text-accent hover:opacity-90">
                  Details
                </Link>
              )}
            />
            {/* DataTable has no footer-row slot, so the aggregate row that
                used to be a <tfoot> <tr> is a summary strip below the table
                instead — same four values (kWh/Max kVA/kVArh/Total), same
                Math.max (not sum) for Max kVA, just not shaped as table
                cells. Works at every width instead of squeezing into the
                scroller the old tfoot needed on mobile.
                <dl>/<dt>/<dd> (not plain spans) for the label/value pairs —
                the same association DataTable.tsx's own collapsed-row <dl>
                already gives its label/value list (DataTable.tsx:172-186),
                so a screen reader gets a real term/definition pairing here
                too, at zero visual cost (dt/dd are still flex items, same
                classes as before). */}
            <dl className="px-6 py-3 border-t border-border-token bg-surface-sunken flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <div className="font-medium text-content">Totals</div>
              {/* Each figure appears only when the run actually contains that
                  utility, so the label set describes the run rather than
                  asserting it is all-electricity. */}
              {elecBills.length > 0 && (
                <div className="flex items-baseline gap-1 text-content-muted">
                  <dt>kWh:</dt>
                  <dd><strong className="text-content font-mono">{formatNumber(totalKwh)}</strong></dd>
                </div>
              )}
              {waterBills.length > 0 && (
                <div className="flex items-baseline gap-1 text-content-muted">
                  <dt>kL:</dt>
                  <dd><strong className="text-content font-mono">{formatNumber(totalKl)}</strong></dd>
                </div>
              )}
              {elecBills.length > 0 && (
                <div className="flex items-baseline gap-1 text-content-muted">
                  <dt>Max kVA:</dt>
                  <dd><strong className="text-content font-mono">{formatNumber(maxKva)}</strong></dd>
                </div>
              )}
              {elecBills.length > 0 && (
                <div className="flex items-baseline gap-1 text-content-muted">
                  <dt>kVArh:</dt>
                  <dd><strong className="text-content font-mono">{formatNumber(totalKvarh)}</strong></dd>
                </div>
              )}
              {/* Money is the one total that is genuinely additive across
                  utilities — cents are cents. Unchanged, over all bills. */}
              <div className="ml-auto font-semibold text-content">
                {formatCurrency(bills.reduce((sum, b) => sum + (b.total_amount_cents || 0), 0))}
              </div>
            </dl>
          </div>
        )}
      </Card>

      {/* Reject Modal */}
      {showRerunModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-content mb-2">Re-Run Bill Run</h2>
            <p className="text-sm text-content-muted mb-4">
              Existing bills will be cleared and the calculation will start again with current attribute values.
              {(billRun?.rerun_count ?? 0) > 0 && (
                /* Final tidy (dark-mode contrast fix): text-amber-700 on
                   this modal's surface-raised(dark) card is 3.02:1 — fails
                   AA. dark:text-amber-300 clears it at 10.52:1. */
                <span className="block mt-1 text-xs text-amber-700 dark:text-amber-300">
                  This bill run has been re-run {billRun!.rerun_count} time{billRun!.rerun_count === 1 ? '' : 's'} already.
                </span>
              )}
            </p>
            <div className="mb-4">
              {/* I3 (dark-mode contrast fix): text-red-600 required-marker is
                  3.14:1 against a dark Card — identical marker already fixed
                  in Tenants.tsx/Login.tsx with dark:text-red-400 (5.48:1). */}
              <label className="block text-sm font-medium text-content mb-1">
                Justification <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <textarea
                value={rerunReason}
                onChange={(e) => setRerunReason(e.target.value)}
                placeholder="Why is this bill run being re-run? (e.g. 'tariff version updated', 'meter assignment corrected')"
                rows={3}
                className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowRerunModal(false); setRerunReason(''); }}
                className="px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRerun}
                disabled={rerunning || !rerunReason.trim()}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {rerunning && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                Re-Run Bill Run
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-content mb-4">Reject Bill Run</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-content mb-1">Reason (optional)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Why is this bill run being rejected?"
                rows={3}
                className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
                className="px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejecting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {rejecting && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                Reject Bill Run
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
